import { Api } from '@/legacy/OldApi';
import { startCase } from 'lodash';
import vOutput from '@/directives/Output';
import { VueDataProvider } from '@/domain/services/VueDataProvider';
import { VueModule } from '@/domain/services/VueModule';
import { ModuleLoader, routes } from '@/routes';
import Vue, { Component, defineComponent, SetupContext, SetupFunction } from 'vue';
import Vuex, { Store } from 'vuex';

/* eslint-disable @typescript-eslint/naming-convention */
declare const Route: string;
declare const window: { App: { [key: string]: unknown } };
/* eslint-enable @typescript-eslint/naming-convention */

let vueInstance: Vue | null = null;
let routeTable: Record<string, ModuleLoader> = {};
let vuePlugins: { readonly [name: string]: unknown } = {};
let childComponents: { readonly [name: string]: Component } = {};
const stubs: { [key: string]: unknown } = {};

let store: Store<unknown> | undefined;

const getCurrentRoute = (): ModuleLoader | undefined => routeTable[Route];

const boot = (vueContext = Vue) => {
	vueContext.directive('output', vOutput);
	vueContext.use(Vuex);
	import('@/lib/store').then(({ default: legacyStore }) => {
		store = legacyStore;
	});
	routeTable = routes as unknown as Record<string, ModuleLoader>;
};

const registerVuePlugins = (plugins: { readonly [name: string]: unknown }) => {
	vuePlugins = plugins || {};
};

const registerComponents = (components: { readonly [name: string]: Component }) => {
	childComponents = components || {};
};

const loadModule = async () => {
	VueDataProvider.bootGlobalData(window.App);

	Api.setup();
	if (store !== undefined) {
		Api.addListener(store);
	}

	const routePromise = getCurrentRoute();

	if (vueInstance) {
		VueModule.destroy(vueInstance);
	}

	if (routePromise !== undefined) {
		const module = await routePromise();
		module.default();

		const name = module.name || Route.replaceAll('.', '-');

		// Here we check if the top level component exists inside the vue-root context element.
		// This is to prevent Vue from trying to mount the module if it is missing,
		// which would cause an error and indicates that the module is not being used.
		// Specifically, after the name variable is being resolved from routes table (which comes from the backend through Route global window variable),
		// we check if the component is present in the DOM.
		// If it is not, we just return and do not load the module.
		if (document.querySelector(`#vue-root ${name}`) === null) {
			console.warn(`Vue module "${name}" not found in #vue-root. Skipping module load.`);
			return;
		}

		vueInstance = VueModule.create({
			...vuePlugins,
			el: '#vue-root',
			name: startCase(name),
			components: {
				[name]: defineComponent({ name, components: childComponents }),
			},
		});
	}
};

const mockView = <T>(key: string, stub: T) => {
	stubs[key] = stub;
};

const getStub = <T>(key: string): T | undefined => stubs[key] as T | undefined;

const makeSetup =
	<Props, View>(setup: SetupFunction<Props, View>, stubName?: string) =>
	(props: Props, ctx: SetupContext): View => {
		if (stubName !== undefined) {
			const stub = getStub<View>(stubName);
			if (stub !== undefined) return stub as View;
		}

		return setup(props, ctx) as View;
	};

// eslint-disable-next-line @typescript-eslint/naming-convention
const Composer = {
	boot,
	registerVuePlugins,
	registerComponents,
	loadModule,
	mockView,
	makeSetup,
};

const useController = Composer.makeSetup;

export { Composer, useController };
